import { useEffect, useRef, useState } from "react";
import { MapPin, CheckCircle2, PencilLine, AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

let mapsPromise;
function loadMaps(key) {
  if (window.google?.maps?.importLibrary) return Promise.resolve();
  if (mapsPromise) return mapsPromise;
  mapsPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&loading=async`;
    s.async = true;
    s.onerror = reject;
    s.onload = () => (window.google?.maps?.importLibrary ? resolve() : reject(new Error("Maps failed")));
    document.head.appendChild(s);
  });
  return mapsPromise;
}

const comp = (parts, ...types) =>
  parts.find((p) => types.some((t) => p.types?.includes(t)))?.longText || "";

export function AddressAutocomplete({ value, onChange, config }) {
  const configured = !!config?.googleMaps?.configured;
  const host = useRef(null);
  const [widgetError, setWidgetError] = useState("");
  const [manual, setManual] = useState(!configured);
  const [form, setForm] = useState({
    formattedAddress: value?.formattedAddress || "",
    street: value?.street || "",
    houseNumber: value?.houseNumber || "",
    postalCode: value?.postalCode || "",
    city: value?.city || "",
    country: value?.country || "",
  });

  useEffect(() => {
    if (!configured || manual) return;
    let disposed = false;
    (async () => {
      try {
        await loadMaps(config.googleMaps.browserKey);
        const { PlaceAutocompleteElement } = await window.google.maps.importLibrary("places");
        if (disposed || !host.current) return;
        const widget = new PlaceAutocompleteElement({ includedRegionCodes: ["be", "nl", "fr"] });
        widget.style.width = "100%";
        host.current.replaceChildren(widget);
        widget.addEventListener("gmp-select", async (event) => {
          const place = event.placePrediction.toPlace();
          await place.fetchFields({ fields: ["id", "formattedAddress", "addressComponents"] });
          const parts = place.addressComponents || [];
          onChange({
            placeId: place.id || "",
            formattedAddress: place.formattedAddress || "",
            street: comp(parts, "route"),
            houseNumber: comp(parts, "street_number"),
            postalCode: comp(parts, "postal_code"),
            city: comp(parts, "locality", "postal_town", "administrative_area_level_2"),
            country: comp(parts, "country"),
            manual: false,
          });
        });
      } catch (e) {
        if (!disposed) setWidgetError(e.message || "Kon adreszoeker niet laden");
      }
    })();
    return () => {
      disposed = true;
    };
  }, [configured, manual, config, onChange]);

  const saveManual = () => {
    if (!form.formattedAddress.trim()) return;
    onChange({ ...form, placeId: "", manual: true });
  };

  const selected = value?.formattedAddress;

  return (
    <div className="space-y-4" data-testid="address-autocomplete">
      {!configured && (
        <div className="flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="address-owner-warning">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            <b>Owner-configuratie:</b> adres-suggesties zijn nog niet actief (Google Maps-sleutel ontbreekt). Handmatige invoer werkt volledig.
          </span>
        </div>
      )}

      {configured && !manual && (
        <div className="space-y-2">
          <Label>Zoek een adres</Label>
          <div ref={host} className="w-full" data-testid="places-widget-host" />
          {widgetError && <p className="text-xs text-re-red">{widgetError}</p>}
          <button type="button" onClick={() => setManual(true)} className="inline-flex items-center gap-1.5 text-xs font-medium text-re-red" data-testid="switch-manual-address">
            <PencilLine className="h-3.5 w-3.5" /> Adres niet gevonden? Handmatig invoeren
          </button>
        </div>
      )}

      {manual && (
        <div className="space-y-3">
          <div className="space-y-2">
            <Label htmlFor="addr-full">Volledig adres</Label>
            <Input id="addr-full" value={form.formattedAddress} onChange={(e) => setForm({ ...form, formattedAddress: e.target.value })} placeholder="Bijv. Keizersgracht 123, 1015 Amsterdam" data-testid="manual-address-input" className="border-re-line" />
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <div className="space-y-1"><Label className="text-xs">Straat</Label><Input value={form.street} onChange={(e) => setForm({ ...form, street: e.target.value })} data-testid="manual-street" className="h-9 border-re-line" /></div>
            <div className="space-y-1"><Label className="text-xs">Huisnr.</Label><Input value={form.houseNumber} onChange={(e) => setForm({ ...form, houseNumber: e.target.value })} data-testid="manual-housenumber" className="h-9 border-re-line" /></div>
            <div className="space-y-1"><Label className="text-xs">Postcode</Label><Input value={form.postalCode} onChange={(e) => setForm({ ...form, postalCode: e.target.value })} data-testid="manual-postal" className="h-9 border-re-line" /></div>
            <div className="space-y-1"><Label className="text-xs">Plaats</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="manual-city" className="h-9 border-re-line" /></div>
            <div className="space-y-1"><Label className="text-xs">Land</Label><Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="manual-country" className="h-9 border-re-line" /></div>
          </div>
          <div className="flex items-center gap-3">
            <Button type="button" onClick={saveManual} disabled={!form.formattedAddress.trim()} data-testid="use-manual-address" className="rounded-full bg-re-charcoal text-white hover:bg-re-ink">
              Gebruik dit handmatig ingevoerde adres
            </Button>
            {configured && (
              <button type="button" onClick={() => setManual(false)} className="text-xs font-medium text-re-muted" data-testid="switch-search-address">
                Terug naar zoeken
              </button>
            )}
          </div>
        </div>
      )}

      {selected && (
        <div className="flex items-start gap-2 rounded-xl border border-emerald-300 bg-emerald-50 px-3 py-2.5" data-testid="selected-address">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
          <div>
            <p className="text-sm font-medium text-re-charcoal">{selected}</p>
            <p className="text-xs text-re-muted">
              {value.manual ? "Handmatig ingevoerd" : "Geselecteerd via adreszoeker"}
              {value.city ? ` · ${value.city}` : ""}
              {value.country ? `, ${value.country}` : ""}
            </p>
          </div>
        </div>
      )}
      <p className="flex items-center gap-1 text-[11px] text-re-muted">
        <MapPin className="h-3 w-3" /> Suggesties met voorrang voor België, Nederland en Frankrijk.
      </p>
    </div>
  );
}
